import React, { useState, useMemo } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  startOfDay,
} from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  CheckCircle,
  Clock,
  AlertCircle,
  Star,
  ArrowDown,
  Calendar as CalendarIcon,
  Search,
  X,
  MapPin,
  SlidersHorizontal,
} from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
  CryptoEvent,
  UserProfile,
  CATEGORY_META,
  CATEGORY_BY_VALUE,
  EventCategory,
} from '../types';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface CalendarProps {
  events: CryptoEvent[];
  user: UserProfile | null;
  onAddEvent: () => void;
  onSelectEvent: (event: CryptoEvent) => void;
}

type ViewMode = 'day' | 'week' | 'month';
type CategoryFilter = 'all' | EventCategory;
type ChainFilter = 'solana_focus' | 'all';

const tileFillStyle = (cat: EventCategory) => {
  const meta = CATEGORY_BY_VALUE[cat];
  return {
    backgroundColor: meta.fill,
    color: meta.textColor === 'white' ? '#ffffff' : '#0b0b0c',
    borderColor: '#0b0b0c',
  } as React.CSSProperties;
};

const StatusIcon: React.FC<{ status: string; light?: boolean }> = ({ status, light }) => {
  const cls = light ? 'text-white/80' : 'text-[#0b0b0c]/70';
  if (status === 'approved') return <CheckCircle className={cn('w-3 h-3', cls)} />;
  if (status === 'pending') return <Clock className={cn('w-3 h-3', cls)} />;
  if (status === 'rejected') return <AlertCircle className={cn('w-3 h-3', cls)} />;
  return null;
};

const formatTime = (event: CryptoEvent): string | null => {
  if (event.time) return event.time;
  return null;
};

export const Calendar: React.FC<CalendarProps> = ({
  events,
  user,
  onAddEvent,
  onSelectEvent,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<ViewMode>('month');
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>('all');
  const [selectedChainFilter, setSelectedChainFilter] = useState<ChainFilter>('solana_focus');
  const [searchQuery, setSearchQuery] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dayDetail, setDayDetail] = useState<Date | null>(null);
  const [hoverEvent, setHoverEvent] = useState<{ event: CryptoEvent; x: number; y: number } | null>(null);

  const isAdmin = user?.role === 'admin';
  const isApprovedClient = user?.role === 'client' && user.isApproved;
  const canAddEvent = isAdmin || isApprovedClient;

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const calendarDays = eachDayOfInterval({ start: startDate, end: endDate });

  const nextDate = () => {
    if (view === 'month') setCurrentDate(addMonths(currentDate, 1));
    else if (view === 'week') setCurrentDate(addWeeks(currentDate, 1));
    else setCurrentDate(addDays(currentDate, 1));
  };

  const prevDate = () => {
    if (view === 'month') setCurrentDate(subMonths(currentDate, 1));
    else if (view === 'week') setCurrentDate(subWeeks(currentDate, 1));
    else setCurrentDate(subDays(currentDate, 1));
  };

  const goToToday = () => setCurrentDate(new Date());

  const filterEventsForDay = (day: Date) => {
    const dayStart = startOfDay(day);
    return events.filter((event) => {
      const eventStart = startOfDay(event.date);
      const matchesDay = isSameDay(eventStart, dayStart);
      const matchesCategory = selectedCategory === 'all' || event.category === selectedCategory;
      const matchesChain =
        selectedChainFilter === 'all' || ['Solana', 'General Crypto'].includes(event.chain);

      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        searchQuery === '' ||
        event.title.toLowerCase().includes(searchLower) ||
        (event.companyName?.toLowerCase().includes(searchLower) ?? false) ||
        (event.description?.toLowerCase().includes(searchLower) ?? false) ||
        (event.location?.toLowerCase().includes(searchLower) ?? false);

      return matchesDay && matchesCategory && matchesChain && matchesSearch;
    });
  };

  const filtersActive = useMemo(
    () => selectedCategory !== 'all' || selectedChainFilter !== 'solana_focus',
    [selectedCategory, selectedChainFilter],
  );

  const dateLabel = useMemo(() => {
    if (view === 'day') return format(currentDate, 'MMM d, yyyy');
    if (view === 'week') {
      const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
      const we = endOfWeek(currentDate, { weekStartsOn: 1 });
      const sameMonth = ws.getMonth() === we.getMonth();
      return sameMonth
        ? `${format(ws, 'MMM d')} – ${format(we, 'd')}`
        : `${format(ws, 'MMM d')} – ${format(we, 'MMM d')}`;
    }
    return format(currentDate, 'MMMM yyyy').toUpperCase();
  }, [view, currentDate]);

  const renderEventTile = (event: CryptoEvent, compact = false) => {
    const time = formatTime(event);
    const fillStyle = tileFillStyle(event.category);
    const isLight = CATEGORY_BY_VALUE[event.category].textColor === 'ink';
    return (
      <button
        key={event.id}
        onClick={(e) => {
          e.stopPropagation();
          onSelectEvent(event);
        }}
        onMouseEnter={(e) => setHoverEvent({ event, x: e.clientX, y: e.clientY })}
        onMouseMove={(e) => setHoverEvent({ event, x: e.clientX, y: e.clientY })}
        onMouseLeave={() => setHoverEvent(null)}
        className={cn(
          'sticker-tile w-full text-left px-2 py-1.5 block group relative',
          compact ? 'min-h-8 sm:min-h-0 text-[10px]' : 'min-h-11 text-[11px]',
        )}
        style={fillStyle}
      >
        <div className="flex items-start justify-between gap-1">
          <span className="font-bold leading-tight truncate">
            {event.featured && (
              <Star
                className={cn('inline w-2.5 h-2.5 mr-0.5 -mt-0.5', isLight ? 'fill-[#ffd24a] text-[#0b0b0c]' : 'fill-[#ffd24a] text-white')}
              />
            )}
            {event.title}
          </span>
          <StatusIcon status={event.status} light={!isLight} />
        </div>
        {!compact && (time || event.location) && (
          <div
            className={cn(
              'mt-0.5 text-[10px] font-medium leading-tight truncate flex items-center gap-1',
              isLight ? 'text-[#0b0b0c]/70' : 'text-white/80',
            )}
          >
            {time && <span>{time}</span>}
            {time && event.location && <span>·</span>}
            {event.location && <span className="truncate">{event.location}</span>}
          </div>
        )}
      </button>
    );
  };

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6">
      {/* Calendar sticker frame */}
      <div className="sticker overflow-hidden bg-white">
        {/* Toolbar */}
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 lg:gap-4 p-3 sm:p-4 lg:p-5 border-b border-[#0b0b0c]/10">
          {/* Left cluster: prev / today / next + date label */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={prevDate}
              aria-label="Previous"
              className="w-11 h-11 sm:w-8 sm:h-8 rounded-full border-[1.5px] border-[#0b0b0c] bg-white hover:bg-[#0b0b0c] hover:text-white flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={goToToday}
              className="px-4 h-11 sm:h-8 pill-outline text-[11px] font-bold uppercase tracking-wider"
            >
              Today
            </button>
            <button
              onClick={nextDate}
              aria-label="Next"
              className="w-11 h-11 sm:w-8 sm:h-8 rounded-full border-[1.5px] border-[#0b0b0c] bg-white hover:bg-[#0b0b0c] hover:text-white flex items-center justify-center transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <span className="ml-0 sm:ml-1 px-3 min-h-11 sm:min-h-8 pill-ink text-[11px] font-bold uppercase tracking-wider inline-flex items-center">
              {dateLabel}
            </span>
          </div>

          {/* View toggle */}
          <div className="grid grid-cols-3 sm:flex sm:items-center bg-[#e9dffa] border-[1.5px] border-[#0b0b0c] rounded-full p-0.5 w-full sm:w-auto">
            {(['day', 'week', 'month'] as const).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={cn(
                  'px-3 sm:px-4 min-h-10 sm:min-h-8 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors',
                  view === v ? 'bg-[#0b0b0c] text-white' : 'text-[#0b0b0c]/70 hover:text-[#0b0b0c]',
                )}
              >
                {v}
              </button>
            ))}
          </div>

          <div className="flex-1" />

          {/* Right cluster: filters + add */}
          <div className="flex items-center gap-2 flex-wrap relative">
            <button
              onClick={() => setFiltersOpen((o) => !o)}
              className={cn(
                'inline-flex items-center gap-1.5 px-4 h-11 sm:h-8 rounded-full text-[11px] font-bold uppercase tracking-wider border-[1.5px] border-[#0b0b0c] transition-colors',
                filtersActive ? 'bg-[#9945ff] text-white' : 'bg-white text-[#0b0b0c] hover:bg-[#0b0b0c] hover:text-white',
              )}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              Filters
              {filtersActive && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-white text-[#0b0b0c] text-[9px] font-black">
                  {(selectedCategory !== 'all' ? 1 : 0) + (selectedChainFilter !== 'solana_focus' ? 1 : 0)}
                </span>
              )}
            </button>

            {filtersOpen && (
              <>
                <button
                  type="button"
                  aria-label="Close filters"
                  className="fixed inset-0 z-30 cursor-default"
                  onClick={() => setFiltersOpen(false)}
                />
                <div className="fixed inset-x-3 bottom-[max(1rem,env(safe-area-inset-bottom))] z-40 sticker bg-white p-4 max-h-[72dvh] overflow-y-auto custom-scrollbar sm:absolute sm:inset-x-auto sm:right-0 sm:top-10 sm:bottom-auto sm:w-[340px]">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55">
                      Category
                    </span>
                    {filtersActive && (
                      <button
                        onClick={() => {
                          setSelectedCategory('all');
                          setSelectedChainFilter('solana_focus');
                        }}
                        className="min-h-10 px-2 text-[10px] font-bold uppercase tracking-wider text-[#9945ff] hover:underline"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className={cn(
                        'px-3 min-h-9 rounded-full text-[10px] font-bold uppercase tracking-wider border-[1.5px] border-[#0b0b0c]',
                        selectedCategory === 'all' ? 'bg-[#0b0b0c] text-white' : 'bg-white text-[#0b0b0c]',
                      )}
                    >
                      All
                    </button>
                    {CATEGORY_META.map((c) => (
                      <button
                        key={c.value}
                        onClick={() => setSelectedCategory(c.value)}
                        className={cn(
                          'px-3 min-h-9 rounded-full text-[10px] font-bold uppercase tracking-wider border-[1.5px] border-[#0b0b0c]',
                          selectedCategory === c.value ? 'bg-[#0b0b0c] text-white' : 'bg-white text-[#0b0b0c]',
                        )}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>

                  <div className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55 mb-2">
                    Scope
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button
                      onClick={() => setSelectedChainFilter('solana_focus')}
                      className={cn(
                        'px-2 min-h-10 rounded-full text-[10px] font-bold uppercase tracking-wider border-[1.5px] border-[#0b0b0c]',
                        selectedChainFilter === 'solana_focus' ? 'bg-[#0b0b0c] text-white' : 'bg-white text-[#0b0b0c]',
                      )}
                    >
                      Solana + Industry
                    </button>
                    <button
                      onClick={() => setSelectedChainFilter('all')}
                      className={cn(
                        'px-2 min-h-10 rounded-full text-[10px] font-bold uppercase tracking-wider border-[1.5px] border-[#0b0b0c]',
                        selectedChainFilter === 'all' ? 'bg-[#0b0b0c] text-white' : 'bg-white text-[#0b0b0c]',
                      )}
                    >
                      All blockchains
                    </button>
                  </div>
                </div>
              </>
            )}

            {canAddEvent ? (
              <button
                onClick={onAddEvent}
                className="inline-flex items-center gap-1.5 px-4 h-11 sm:h-8 rounded-full bg-[#9945ff] text-white border-[1.5px] border-[#0b0b0c] text-[11px] font-bold uppercase tracking-wider hover:bg-[#5b29a6] transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Submit
              </button>
            ) : !user ? (
              <button
                onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
                className="inline-flex items-center gap-1.5 px-4 h-11 sm:h-8 rounded-full bg-[#9945ff] text-white border-[1.5px] border-[#0b0b0c] text-[11px] font-bold uppercase tracking-wider hover:bg-[#5b29a6] transition-colors"
              >
                Submit
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
            ) : null}
          </div>
        </div>

        {/* Search */}
        <div className="px-3 sm:px-4 lg:px-5 py-3 border-b border-[#0b0b0c]/10">
          <div className="relative max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b0b0c]/45" />
            <input
              type="text"
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-11 sm:h-9 pl-10 pr-10 rounded-full border-[1.5px] border-[#0b0b0c] bg-white text-[13px] font-medium placeholder:text-[#0b0b0c]/40 focus:outline-none focus:ring-2 focus:ring-[#9945ff]/30"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-0.5 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-9 sm:h-9 inline-flex items-center justify-center hover:bg-[#0b0b0c]/5 rounded-full"
                aria-label="Clear search"
              >
                <X className="w-3 h-3 text-[#0b0b0c]/55" />
              </button>
            )}
          </div>
        </div>

        {/* Month View */}
        {view === 'month' && (
          <>
            <div className="grid grid-cols-7 bg-[#f3ecfc]">
              {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((day) => (
                <div
                  key={day}
                  className="py-2 text-center text-[10px] font-bold uppercase tracking-wider text-[#0b0b0c]/55 border-r border-[#0b0b0c]/10 last:border-r-0"
                >
                  <span className="hidden sm:inline">{day}</span>
                  <span className="sm:hidden">{day.charAt(0)}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 border-t border-[#0b0b0c]/10">
              {calendarDays.map((day, idx) => {
                const dayEvents = filterEventsForDay(day);
                const isCurrentMonth = isSameMonth(day, monthStart);
                const isToday = isSameDay(day, new Date());

                return (
                  <div
                    key={idx}
                    className={cn(
                      'min-h-[92px] sm:min-h-[140px] p-1.5 sm:p-2 border-r border-b border-[#0b0b0c]/10 last:border-r-0 transition-colors hover:bg-[#f3ecfc]/60 min-w-0',
                      !isCurrentMonth && 'bg-[#f3ecfc]/40',
                      isToday && 'bg-[#14f195]/15',
                    )}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <button
                        type="button"
                        onClick={() => setDayDetail(day)}
                        aria-label={`View events for ${format(day, 'MMMM d, yyyy')}`}
                        className={cn(
                          'inline-flex items-center justify-center text-[11px] font-bold rounded-full w-10 h-10 sm:w-6 sm:h-6',
                          isToday
                            ? 'bg-[#9945ff] text-white'
                            : isCurrentMonth
                            ? 'text-[#0b0b0c]'
                            : 'text-[#0b0b0c]/35',
                        )}
                      >
                        {format(day, 'd')}
                      </button>
                      {dayEvents.length > 3 && (
                        <span className="text-[9px] font-bold uppercase tracking-wider text-[#0b0b0c]/55">
                          +{dayEvents.length - 3}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col gap-1">
                      {dayEvents.slice(0, 3).map((event) => renderEventTile(event, true))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Week View */}
        {view === 'week' && (
          <div className="grid grid-cols-1 md:grid-cols-7 border-t border-[#0b0b0c]/10">
            {eachDayOfInterval({
              start: startOfWeek(currentDate, { weekStartsOn: 1 }),
              end: endOfWeek(currentDate, { weekStartsOn: 1 }),
            }).map((day, idx) => {
              const dayEvents = filterEventsForDay(day);
              const isToday = isSameDay(day, new Date());
              return (
                <div
                  key={idx}
                  className={cn(
                    'min-h-[200px] md:min-h-[360px] p-3 border-r border-b border-[#0b0b0c]/10 last:border-r-0',
                    isToday && 'bg-[#14f195]/15',
                  )}
                >
                  <div className="text-center mb-3">
                    <p className="text-[9px] font-bold uppercase tracking-wider text-[#0b0b0c]/55">
                      {format(day, 'EEE')}
                    </p>
                    <span
                      className={cn(
                        'inline-flex items-center justify-center text-base font-bold rounded-full w-8 h-8 mt-0.5',
                        isToday ? 'bg-[#9945ff] text-white' : 'text-[#0b0b0c]',
                      )}
                    >
                      {format(day, 'd')}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {dayEvents.map((event) => renderEventTile(event, false))}
                    {dayEvents.length === 0 && (
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#0b0b0c]/35 text-center pt-4">
                        No events
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Day View */}
        {view === 'day' && (
          <div className="p-6 border-t border-[#0b0b0c]/10 bg-[#f3ecfc]/40">
            <div className="max-w-3xl mx-auto space-y-3">
              {filterEventsForDay(currentDate).map((event) => (
                <button
                  key={event.id}
                  onClick={() => onSelectEvent(event)}
                  className="sticker bg-white w-full text-left p-4 sm:p-5 group"
                >
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span
                      className="px-2.5 py-0.5 rounded-full border-[1.5px] border-[#0b0b0c] text-[10px] font-bold uppercase tracking-wider"
                      style={tileFillStyle(event.category)}
                    >
                      {CATEGORY_BY_VALUE[event.category].label}
                    </span>
                    {event.chain && (
                      <span className="px-2.5 py-0.5 rounded-full pill-outline text-[10px] font-bold uppercase tracking-wider">
                        {event.chain === 'General Crypto' ? 'Industry-wide' : event.chain}
                      </span>
                    )}
                    {event.featured && (
                      <span className="px-2.5 py-0.5 rounded-full pill-outline text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1">
                        <Star className="w-3 h-3 fill-[#ffd24a] text-[#0b0b0c]" /> Featured
                      </span>
                    )}
                    <div className="ml-auto"><StatusIcon status={event.status} /></div>
                  </div>
                  <h3 className="font-bold text-xl sm:text-2xl uppercase tracking-tight text-[#0b0b0c] mb-1">
                    {event.title}
                  </h3>
                  {(formatTime(event) || event.location) && (
                    <p className="text-sm font-medium text-[#0b0b0c]/70 mb-2 flex items-center gap-2 flex-wrap">
                      {formatTime(event) && <span>{formatTime(event)}</span>}
                      {event.location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {event.location}
                        </span>
                      )}
                      {event.companyName && <span>· {event.companyName}</span>}
                    </p>
                  )}
                  {event.description && (
                    <p className="text-sm text-[#0b0b0c]/65 leading-relaxed line-clamp-2">
                      {event.description}
                    </p>
                  )}
                </button>
              ))}
              {filterEventsForDay(currentDate).length === 0 && (
                <div className="py-16 text-center">
                  <div className="w-16 h-16 sticker bg-white rounded-full inline-flex items-center justify-center mb-4">
                    <CalendarIcon className="w-7 h-7 text-[#0b0b0c]/40" />
                  </div>
                  <h3 className="font-bold text-2xl uppercase tracking-tight text-[#0b0b0c] mb-1">
                    No events
                  </h3>
                  <p className="text-sm font-medium text-[#0b0b0c]/55">
                    Nothing scheduled for this day yet.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Category legend */}
      <div className="sticker bg-white px-6 py-5 flex flex-wrap items-center gap-x-6 gap-y-3 justify-center">
        {CATEGORY_META.map((c) => (
          <div key={c.value} className="flex items-center gap-2">
            <span
              className="w-4 h-4 rounded-full border-[1.5px] border-[#0b0b0c]"
              style={{ backgroundColor: c.fill }}
            />
            <span className="text-sm font-bold uppercase tracking-wider text-[#0b0b0c]">
              {c.label}
            </span>
          </div>
        ))}
      </div>

      {/* Hover tooltip */}
      {hoverEvent?.event.description && (
        <div
          className="hidden sm:block fixed z-50 pointer-events-none sticker bg-white p-3 max-w-xs"
          style={{ left: hoverEvent.x + 14, top: hoverEvent.y + 14 }}
        >
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#0b0b0c]/55 mb-1">
            {CATEGORY_BY_VALUE[hoverEvent.event.category].label}
          </p>
          <p className="text-sm font-bold uppercase tracking-tight text-[#0b0b0c] leading-tight mb-1">
            {hoverEvent.event.title}
          </p>
          <p className="text-[12px] text-[#0b0b0c]/70 leading-snug">
            {hoverEvent.event.description.slice(0, 160)}
            {hoverEvent.event.description.length > 160 && '...'}
          </p>
        </div>
      )}

      {/* Day detail modal */}
      {dayDetail && (
        <DayDetailModal
          day={dayDetail}
          events={filterEventsForDay(dayDetail)}
          onClose={() => setDayDetail(null)}
          onSelectEvent={(e) => {
            setDayDetail(null);
            onSelectEvent(e);
          }}
        />
      )}
    </div>
  );
};

interface DayDetailModalProps {
  day: Date;
  events: CryptoEvent[];
  onClose: () => void;
  onSelectEvent: (e: CryptoEvent) => void;
}

const DayDetailModal: React.FC<DayDetailModalProps> = ({ day, events, onClose, onSelectEvent }) => {
  return (
    <div
      className="fixed inset-0 z-50 bg-[#0b0b0c]/40 flex items-end sm:items-center justify-center safe-overlay"
    >
      <button
        type="button"
        aria-label="Close day details"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />
      <div
        className="sticker bg-[#e9dffa] max-w-lg w-full mobile-sheet overflow-y-auto custom-scrollbar relative"
      >
        <div className="flex items-start justify-between gap-3 p-4 sm:p-5 border-b border-[#0b0b0c]/15">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#0b0b0c]/55">
              {format(day, 'EEEE')}
            </p>
            <h2 className="font-bold text-2xl sm:text-3xl uppercase tracking-tight text-[#0b0b0c]">
              {format(day, 'MMM d, yyyy').toUpperCase()}
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-11 h-11 sm:w-8 sm:h-8 rounded-full border-[1.5px] border-[#0b0b0c] bg-white hover:bg-[#0b0b0c] hover:text-white flex items-center justify-center transition-colors shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-2.5">
          {events.length === 0 ? (
            <p className="text-sm text-[#0b0b0c]/55 text-center py-6">No events on this day.</p>
          ) : (
            events.map((event) => {
              const isLight = CATEGORY_BY_VALUE[event.category].textColor === 'ink';
              const time = formatTime(event);
              return (
                <button
                  key={event.id}
                  onClick={() => onSelectEvent(event)}
                  className="sticker-tile w-full min-h-11 text-left p-3"
                  style={tileFillStyle(event.category)}
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {event.featured && (
                      <Star className="w-3.5 h-3.5 fill-[#ffd24a] text-[#0b0b0c]" />
                    )}
                    <span className="font-bold uppercase text-[10px] tracking-wider opacity-70">
                      {CATEGORY_BY_VALUE[event.category].label}
                    </span>
                    {time && (
                      <span className={cn('text-[10px] font-bold', isLight ? 'text-[#0b0b0c]/65' : 'text-white/80')}>
                        {time}
                      </span>
                    )}
                    <div className="ml-auto shrink-0">
                      <StatusIcon status={event.status} light={!isLight} />
                    </div>
                  </div>
                  <p className="font-bold uppercase tracking-tight text-base leading-tight">
                    {event.title}
                  </p>
                  {event.location && (
                    <p
                      className={cn(
                        'mt-0.5 text-[11px] font-medium flex items-center gap-1 break-words',
                        isLight ? 'text-[#0b0b0c]/65' : 'text-white/80',
                      )}
                    >
                      <MapPin className="w-3 h-3" />
                      {event.location}
                    </p>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
